import usePaymentOrderOptions from "../../gestion-payments/usePaymentOrderOptions";
import usePaymentFormChoices from "../../gestion-payments/usePaymentFormChoices";
import BusinessResourceNotice from "../../components/BusinessResourceNotice";
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  message,
  Row,
  Col,
  Alert,
} from "antd";
import dayjs from "dayjs";
import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { useCommercialTreasuryAccountSuggestion } from "../../hooks/useCommercialTreasurySuggestion";

interface VentePaymentModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
  onPaymentAdded: () => Promise<void>;
}

// Interface pour le mode de paiement qui correspond à la structure de la table

// Fonction pour générer une clé d'idempotence
const generateIdempotencyKey = (): string => {
  const timestamp = new Date().getTime();
  const random = Math.floor(Math.random() * 1000000);
  return `${timestamp}-${random}`;
};

const VentePaymentModal: React.FC<VentePaymentModalProps> = ({
  visible,
  onClose,
  order,
  onPaymentAdded,
}) => {
  const [form] = Form.useForm();
  const { selectedCompany, selectedWarehouse } = useSelection();

  const [submitting, setSubmitting] = useState(false);
  const requestId = useRef(generateIdempotencyKey());
  const submissionInProgress = useRef(false);
  const { user, hasPermission } = useAuth();
  const canCreateIncomingPayment = hasPermission(
    "Gestion Commerciale.Ventes.PaiementsEntrants.create"
  );
  const companyId = order?.company_id || selectedCompany;
  const warehouseId = order?.warehouse_id || selectedWarehouse;
  const contextMatches = (!selectedCompany || Number(companyId) === Number(selectedCompany)) &&
    (!selectedWarehouse || Number(warehouseId) === Number(selectedWarehouse));
  const balances = usePaymentOrderOptions({ userId: user?.id, companyId, warehouseId,
    paymentType: "in", entityId: order?.user_id, orderId: order?.id,
    active: visible, enabled: canCreateIncomingPayment && contextMatches });
  const choices = usePaymentFormChoices({ userId: user?.id, companyId, warehouseId,
    paymentType: "in", includeEntities: false, active: visible,
    enabled: canCreateIncomingPayment && contextMatches });
  const { paymentModes, treasuryAccounts, loading: accountsLoading } = choices;
  const { onModeChange: suggestTreasuryAccount } =
    useCommercialTreasuryAccountSuggestion(form, {
      warehouseId: order?.warehouse_id || selectedWarehouse,
      enabled: visible,
      modeField: "payment_mode_id",
      accountField: "treasury_account_id",
      accounts: treasuryAccounts,
    });

  // Calculer le statut de paiement en fonction du montant payé et du montant total
  const loading = choices.loading;
  const remaining = balances.orders[0]?.due_amount;
  const openedAt = useRef(0);
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    if (visible && order?.id) {
      openedAt.current = Date.now();
      requestId.current = generateIdempotencyKey();
      setPaymentError("");
      form.resetFields();
      form.setFieldsValue({ date: dayjs() });
    }
  }, [visible, order?.id, user?.id, companyId, warehouseId, form]);
  useEffect(() => {
    if (visible && !balances.loading && balances.updatedAt >= openedAt.current && remaining !== undefined && form.getFieldValue("amount") == null &&
      !form.isFieldTouched("amount")) form.setFieldsValue({ amount: remaining });
  }, [visible, remaining, balances.loading, balances.updatedAt, form]);

  // Soumettre le formulaire
  const onFinish = async (values: any) => {
    if (!canCreateIncomingPayment) {
      message.error(
        "Vous n'avez pas la permission d'ajouter un paiement client."
      );
      return;
    }
    // Prevent multiple submissions of the same form
    if (submissionInProgress.current) {
      return;
    }

    try {
      submissionInProgress.current = true;
      setSubmitting(true);

      // Vérifier si un paiement similaire existe déjà pour cette commande
      try {
        const scopedCompanyId = order.company_id || selectedCompany;
        const scopedWarehouseId = order.warehouse_id || selectedWarehouse;
        const checkResponse = await apiClient.get("/api/payments", {
          params: {
            order_id: order.id,
            limit: 100,
            company_id: scopedCompanyId,
            warehouse_id: scopedWarehouseId,
          },
        });
        const existingPaymentsRaw =
          checkResponse.data?.payments || checkResponse.data;
        const existingPayments = Array.isArray(existingPaymentsRaw)
          ? existingPaymentsRaw
          : [];

        // Verifier si un paiement similaire existe deja
        const similarPayment = existingPayments.find(
          (payment: any) =>
            payment.payment_mode_id === values.payment_mode_id &&
            Math.abs(payment.amount - values.amount) < 0.01 &&
            payment.date === dayjs(values.date).format("YYYY-MM-DD")
        );

        if (similarPayment) {
          console.warn(
            "[VentePaymentModal] Un paiement similaire existe deja:",
            similarPayment
          );
          message.warning(
            "Attention : un paiement similaire semble deja enregistre. Veuillez verifier."
          );

          // Option: arreter la soumission ou demander confirmation
          const confirmContinue = window.confirm(
            "Un paiement similaire existe deja. Voulez-vous continuer ?"
          );
          if (!confirmContinue) {
            setSubmitting(false);
            return;
          }
        }
      } catch (checkError) {
        console.error(
          "[VentePaymentModal] Erreur lors de la vérification des paiements existants:",
          checkError
        );
        // Continuer malgré l'erreur de vérification
      }

      await balances.verify([{ order_id: order.id, amount: Number(values.amount) }]);
      setPaymentError("");

      // Au lieu de faire deux appels séparés, utiliser l'endpoint combiné
      const processPaymentData = {
        payment: {
          company_id: companyId,
          warehouse_id: warehouseId,
          payment_type: "in", // Paiement entrant (depuis un client) pour les ventes
          date: dayjs(values.date).format("YYYY-MM-DD"),
          amount: values.amount,
          payment_mode_id: values.payment_mode_id,
          treasury_account_id: values.treasury_account_id,
          user_id: order.user_id, // ID du client
          notes: values.notes || "",
          staff_user_id: user?.id || null,
          orders: [
            {
              order_id: order.id,
              amount: values.amount,
            },
          ],
        },
        order: { id: order.id },
      };

      // Frontend Log: Detail data sent to backend

      await apiClient.post(
        "/api/payments/process-order-payment",
        processPaymentData,
        {
          headers: {
            "X-Idempotency-Key": requestId.current,
          },
        }
      );

      void balances.invalidate();

      // Réinitialiser le formulaire et fermer le modal
      form.resetFields();
      message.success("Paiement ajouté avec succès");

      try {
        // Mettre à jour les données
        await onPaymentAdded();
      } catch (refreshError) {
        console.error(
          "Erreur lors du rafraîchissement des données:",
          refreshError
        );
        // Pas de message d'erreur ici pour éviter la confusion
      }

      onClose();
    } catch (error: any) {
      console.error(
        "[VentePaymentModal] Erreur lors de l'ajout du paiement:",
        error
      );

      // Afficher un message d'erreur plus détaillé
      let errorMessage = "Une erreur est survenue lors de l'ajout du paiement";

      errorMessage = error.response?.data?.error || error.message || errorMessage;
      setPaymentError(errorMessage);
      if (error.response?.data?.code === "ORDER_PAYMENT_BALANCE_CHANGED") {
        void balances.refresh().catch(() => {});
      }

      // Afficher le message d'erreur à l'utilisateur
      message.error(`Erreur: ${errorMessage}`);
    } finally {
      setSubmitting(false);
      submissionInProgress.current = false;
    }
  };

  // Rendu des options du sélecteur de mode de paiement avec indication de l'entreprise
  const renderPaymentModeOptions = () => {
    if (loading) {
      return <Select.Option value="">Chargement...</Select.Option>;
    }

    if (paymentModes.length === 0 && choices.status !== "verified") {
      return <Select.Option value="" disabled>Modes non vérifiés</Select.Option>;
    }
    if (paymentModes.length === 0) {
      return (
        <Select.Option value="" disabled>
          Aucun mode de paiement disponible
        </Select.Option>
      );
    }

    // Trier les modes de paiement : d'abord les modes spécifiques à l'entreprise, puis les modes génériques
    const sortedModes = [...paymentModes].sort((a, b) => {
      // Priorité aux modes de l'entreprise
      if (a.company_id && !b.company_id) return -1;
      if (!a.company_id && b.company_id) return 1;

      // Ensuite par nom
      return a.name.localeCompare(b.name);
    });

    return sortedModes.map((mode) => (
      <Select.Option key={mode.id} value={mode.id.toString()}>
        {mode.name}
        {mode.company_id ? " [Spécifique]" : " [Général]"}
      </Select.Option>
    ));
  };

  return (
    <Modal title="Ajouter un paiement" open={visible} onCancel={onClose} footer={null} width={800}>
      <Form form={form} onFinish={onFinish} layout="vertical">
        {!contextMatches && <Alert type="warning" showIcon message="Le contexte a changé. Fermez puis rouvrez le formulaire." />}
        <BusinessResourceNotice resource={balances} label="commandes et soldes" />
        <BusinessResourceNotice resource={choices} label="listes de choix" />
        {remaining !== undefined && <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={`Restant dû : ${remaining.toLocaleString("fr-FR")} XOF`} />}
        {paymentError && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={paymentError} />}

        {choices.status === "verified" && treasuryAccounts.length === 0 && (
          <Alert
            message="Aucun compte de trésorerie"
            description="Créez un compte de trésorerie pour enregistrer ce paiement."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="date"
              label="Date"
              rules={[
                { required: true, message: "Veuillez sélectionner une date" },
              ]}
            >
              <DatePicker />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="amount"
              label="Montant"
              rules={[
                { required: true, message: "Veuillez saisir le montant" },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                formatter={(value) =>
                  value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ") : ""
                }
                parser={(value: any) =>
                  value ? Number(value.replace(/\s/g, "")) : 0
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="treasury_account_id"
              label="Compte de trésorerie"
              rules={[
                {
                  required: true,
                  message: "Veuillez sélectionner un compte de trésorerie",
                },
              ]}
            >
              <Select
                placeholder="Sélectionner un compte"
                loading={accountsLoading}
                disabled={treasuryAccounts.length === 0}
              >
                {treasuryAccounts.map((account: any) => (
                  <Select.Option key={account.id} value={account.id}>
                    {account.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="payment_mode_id"
              label="Mode de paiement"
              rules={[
                {
                  required: true,
                  message: "Veuillez sélectionner un mode de paiement",
                },
              ]}
            >
              <Select
                onChange={suggestTreasuryAccount}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const childrenContent = option?.children;
                  // Ensure childrenContent is a string and option exists
                  if (option && typeof childrenContent === "string") {
                    // Use the string value after type check
                    const childrenString: string = childrenContent;
                    return childrenString
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  // If children is not a string or option is undefined, don't match
                  return false;
                }}
              >
                {renderPaymentModeOptions()}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={!canCreateIncomingPayment || !contextMatches || !balances.data || balances.loading || remaining === 0 || !choices.data}
            >
              Soumettre
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default VentePaymentModal;
